import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CollectionSummary as Collection } from '../types';

export default function Collections() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await fetch('/api/collections', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        throw new Error(errorData.error || 'Failed to fetch collections');
      }
      
      const data = await response.json();
      setCollections(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setCollections([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreateError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/collections', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newCollection),
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to create collection');
      
      setCollections(prev => [...prev, data as Collection]);
      setIsCreating(false);
      setNewCollection({ name: '', description: '' });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setCreateError(errorMsg);
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SkeletonCard = () => (
    <div className="card p-6 animate-pulse">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Game Collections</h1>
        {user && !user.isGuest && (
          <button
            onClick={() => setIsCreating(true)}
            className="btn-primary w-full sm:w-auto"
          >
            New Collection
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {isCreating && (
        <form onSubmit={handleCreateCollection} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Collection Name
            </label>
            <input
              type="text"
              id="name"
              value={newCollection.name}
              onChange={(e) => setNewCollection(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description (optional)
            </label>
            <textarea
              id="description"
              value={newCollection.description}
              onChange={(e) => setNewCollection(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
              rows={3}
            />
          </div>

          {createError && (
            <p className="text-sm text-red-600 dark:text-red-400">{createError}</p>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="btn-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                'Create'
              )}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          // Show skeleton cards while loading
          [...Array(3)].map((_, i) => <SkeletonCard key={i} />)
        ) : (
          // Render actual collection cards
          collections.map((collection) => (
            <div
              key={collection.id}
              onClick={() => navigate(`/collections/${collection.id}`)}
              className="card p-6 cursor-pointer"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{collection.name}</h2>
              {collection.description && (
                <p className="mt-2 text-gray-600 dark:text-gray-300">{collection.description}</p>
              )}
              <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
                <span>{collection.game_count} games</span>
                <span className="mx-2">•</span>
                <span>Created {new Date(collection.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))
        )}

        {!isLoading && collections.length === 0 && !isCreating && (
          <div className="col-span-full text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No collections yet</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Create your first collection to start tracking game scores!
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 